import React from "react";
import { X, PenLine } from "lucide-react";

function fmtDate(fmt) {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  switch (fmt) {
    case "MM/DD/YYYY": return `${mm}/${dd}/${yyyy}`;
    case "YYYY-MM-DD": return `${yyyy}-${mm}-${dd}`;
    case "DD.MM.YYYY": return `${dd}.${mm}.${yyyy}`;
    default: return `${dd}/${mm}/${yyyy}`;
  }
}

const Ph = ({ children }) => <span className="text-slate-400">[{children}]</span>;

export default function ContractPreview({ legal, company, deposit, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="contract-preview">
      <div className="fixed inset-0 bg-violet-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[760px] my-4">
        <button onClick={onClose} className="absolute -top-2 -right-2 z-10 h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-slate-700 transition-colors shadow-lg" data-testid="contract-preview-close" aria-label="Fermer">
          <X className="h-4 w-4" />
        </button>

        {/* Feuille A4 */}
        <div className="bg-white rounded-lg shadow-2xl px-10 sm:px-14 py-12 text-slate-800" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
          {/* En-tête */}
          <div className="flex items-start justify-between gap-6 pb-6 border-b-2 border-slate-900">
            <div>
              {company.logo_data_url ? (
                <img src={company.logo_data_url} alt="Logo" className="h-12 object-contain mb-2" />
              ) : (
                <div className="text-xl font-bold tracking-tight">{company.name || <Ph>Nom de l'entreprise</Ph>}</div>
              )}
              <div className="text-[11px] leading-relaxed text-slate-500">
                {company.name && company.logo_data_url && <div className="font-semibold text-slate-700">{company.name}</div>}
                <div>{company.address || <Ph>Adresse du siège</Ph>}</div>
                <div>SIRET : {company.registration_number || <Ph>À compléter</Ph>}{company.vat_number ? ` · TVA : ${company.vat_number}` : ""}</div>
                <div>{[company.phone, company.email].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Contrat n°</div>
              <div className="text-sm font-semibold"><Ph>2026-0001</Ph></div>
              <div className="text-[11px] text-slate-500 mt-1">{fmtDate(company.date_format)}</div>
            </div>
          </div>

          <h1 className="text-center text-lg font-bold tracking-[0.15em] uppercase mt-8 mb-6">Contrat de location de véhicule</h1>

          {/* Parties */}
          <div className="grid grid-cols-2 gap-4 text-[12px]">
            <div className="border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-sans font-bold mb-1">Le Loueur</div>
              <div className="font-semibold">{company.name || <Ph>Entreprise</Ph>}</div>
              <div className="text-slate-500">{company.legal_form || <Ph>Forme juridique</Ph>}</div>
            </div>
            <div className="border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-sans font-bold mb-1">Le Locataire</div>
              <div className="font-semibold"><Ph>Nom du client</Ph></div>
              <div className="text-slate-500"><Ph>Permis n°</Ph> · <Ph>Adresse</Ph></div>
            </div>
          </div>

          {/* Véhicule & durée */}
          <div className="mt-4 border border-slate-200 rounded p-3 text-[12px]">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-sans font-bold mb-1">Véhicule & période de location</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
              <div>Véhicule : <Ph>Marque, modèle</Ph></div>
              <div>Immatriculation : <Ph>AA-000-AA</Ph></div>
              <div>Départ : <Ph>Date et heure</Ph></div>
              <div>Retour : <Ph>Date et heure</Ph></div>
            </div>
          </div>

          {/* Conditions financières */}
          <div className="mt-4 border border-slate-200 rounded p-3 text-[12px]">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-sans font-bold mb-2">Conditions financières</div>
            <div className="space-y-1">
              <div className="flex justify-between"><span>Prix de la location</span><span className="font-semibold"><Ph>Montant</Ph></span></div>
              {legal.show_deposit && (
                <div className="flex justify-between" data-testid="preview-deposit"><span>Caution (empreinte bancaire)</span><span className="font-semibold">{deposit ? `${deposit} €` : <Ph>Montant</Ph>}</span></div>
              )}
              {legal.show_franchise && (
                <div className="flex justify-between" data-testid="preview-franchise"><span>Franchise en cas de sinistre</span><span className="font-semibold">{Number(legal.franchise_amount) > 0 ? `${legal.franchise_amount} €` : <Ph>Montant</Ph>}</span></div>
              )}
              {legal.show_extra_km && (
                <div className="flex justify-between" data-testid="preview-extrakm">
                  <span>Kilométrage inclus : {Number(legal.included_km_per_day) > 0 ? `${legal.included_km_per_day} km/jour` : <Ph>km/jour</Ph>}</span>
                  <span className="font-semibold">Km supplémentaire : {Number(legal.extra_km_price) > 0 ? `${legal.extra_km_price} €/km` : <Ph>€/km</Ph>}</span>
                </div>
              )}
            </div>
          </div>

          {/* Corps du contrat */}
          <div className="mt-5 text-[11px] leading-relaxed text-slate-600 whitespace-pre-wrap max-h-44 overflow-hidden relative">
            {legal.contract_text
              ? legal.contract_text.slice(0, 1200)
              : "ARTICLE 1 — OBJET\nLe présent contrat a pour objet la location du véhicule désigné ci-dessus…\n\nARTICLE 2 — OBLIGATIONS DU LOCATAIRE\nLe locataire s'engage à utiliser le véhicule en bon père de famille…\n\n(Rédigez votre modèle de contrat dans l'onglet Juridique, ou laissez l'IA le préparer.)"}
            <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-white to-transparent" />
          </div>

          {/* États des lieux */}
          {legal.show_inspection_page && (
            <div className="mt-5 border border-dashed border-slate-300 rounded p-3" data-testid="preview-inspection">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-sans font-bold mb-2">Annexe — État des lieux (départ / retour)</div>
              <div className="grid grid-cols-2 gap-3 text-[11px] text-slate-500">
                <div>Carrosserie : <Ph>schéma</Ph> · Niveau carburant : ▢▢▢▢▢ · Kilométrage : <Ph>km</Ph></div>
                <div>Intérieur : <Ph>observations</Ph> · Équipements : <Ph>liste</Ph></div>
              </div>
            </div>
          )}

          {/* CGV annexées */}
          {legal.attach_cgv && (
            <div className="mt-3 text-[10px] text-slate-400 italic" data-testid="preview-cgv">
              Annexe : Conditions générales de location ({legal.cgv ? `${Math.max(1, Math.ceil(legal.cgv.length / 3000))} page(s)` : "à rédiger dans l'onglet Juridique"}) — remises et acceptées par le locataire.
            </div>
          )}

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-6 mt-8">
            {["Le Loueur", "Le Locataire"].map((who) => (
              <div key={who}>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-sans font-bold">{who}</div>
                <div className="text-[10px] text-slate-400 mb-1">« Lu et approuvé »</div>
                <div className="h-16 border border-slate-200 rounded flex items-center justify-center text-slate-300">
                  <PenLine className="h-4 w-4" />
                </div>
              </div>
            ))}
          </div>

          {/* Micro-mention */}
          <div className="mt-10 pt-3 border-t border-slate-100 text-center">
            <span className="text-[9px] tracking-wide text-slate-300 font-sans">Conçu avec junago.com</span>
          </div>
        </div>
      </div>
    </div>
  );
}
